import { useEffect, useState } from 'react';
import { useDataTable, type DataTableRefs } from '@odo/shared-ui';
import { useInfiniteUsers, useSendPasswordReset, useUsersList } from '../../hooks/useUsers';
import { usePageTitle } from '../../hooks/usePageTitle';
import { DATA_TABLE_TALL_ROW_HEIGHT } from '../../hooks/useViewportPageSize';
import { useViewportTableLimit } from '../../hooks/useViewportTableLimit';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { useToast } from '../../providers/ToastProvider';
import type { UserListItem, UsersSortBy } from '../../services/users';
import { apiErrorMessage } from '../../utils/apiError';
import {
    PracticeFilterPill,
    type SelectedPractice
} from '../../components/accounts/users/PracticeFilterPill';
import { UserDrawerHost } from '../../components/accounts/users/UserDrawerHost';
import { useUserDrawerController } from '../../components/accounts/users/useUserDrawerController';
import { UsersTable } from '../../components/accounts/users/UsersTable';

const SEARCH_DEBOUNCE_MS = 300;
// Starting limit only: the paged fetch waits for the viewport measurement.
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;

type UserFilters = {
    practiceId: string | null;
};

export function UsersList() {
    usePageTitle('Users');
    const { setTitle, setSubtitle } = usePageHeader();
    const { showToast } = useToast();

    // No sessionStorage persistence here, pre- or post-migration - this page
    // never had it, unlike PracticesList's sort/order. `defaultSort` mirrors
    // the pre-migration default (`sortBy = 'name'`), even though only
    // "Last activity" ever emits a `toggleSort('activity')` - see
    // userColumns.tsx.
    const table = useDataTable<UsersSortBy, UserFilters>({
        defaultSort: { by: 'name', order: 'asc' },
        defaultLimit: VIEWPORT_DEFAULT_PAGE_SIZE,
        defaultFilters: { practiceId: null }
    });
    const { practiceId } = table.filters;
    const isInfinite = table.mode === 'infinite';

    const [searchInput, setSearchInput] = useState('');
    const { setSearch: commitSearch } = table;
    useEffect(() => {
        const handle = setTimeout(() => commitSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchInput, commitSearch]);

    // The pill needs the practice's name too (empty-state copy), which the
    // scalar `practiceId` filter can't carry - kept alongside it rather than
    // folded in, same split CommunicationsList uses for its bespoke filters.
    const [practiceFilter, setPracticeFilter] = useState<SelectedPractice | null>(null);
    const { setFilter } = table;
    const handlePracticeFilterChange = (practice: SelectedPractice | null) => {
        setPracticeFilter(practice);
        setFilter('practiceId', practice?.id ?? null);
    };

    const {
        isReady: pageSizeReady,
        tableCardRef,
        tableHeaderRef,
        tableBodyRef,
        paginationRef
    } = useViewportTableLimit(table, { rowHeight: DATA_TABLE_TALL_ROW_HEIGHT });

    const { page, limit, sort, order } = table.params;
    const sharedUserParams = {
        limit,
        search: table.search || undefined,
        sort,
        order,
        practice: practiceId ? [practiceId] : undefined
    };
    // Both hooks are called unconditionally (React's rules of hooks) and only
    // one is ever `enabled` - same split as PracticesList's paged/infinite pair.
    const paged = useUsersList({
        ...sharedUserParams,
        page,
        enabled: !isInfinite && pageSizeReady
    });
    const infinite = useInfiniteUsers({ ...sharedUserParams, enabled: isInfinite });

    const users = isInfinite ? infinite.users : paged.users;
    const meta = isInfinite ? infinite.meta : paged.meta;
    const isLoading = isInfinite ? infinite.isLoading : paged.isLoading;

    const sendPasswordReset = useSendPasswordReset();

    const totalPages = Math.max(1, Math.ceil(meta.total / limit));

    useEffect(() => {
        setTitle('Users');
        setSubtitle(`${meta.total} total`);
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [meta.total, setTitle, setSubtitle]);

    const hasFilters = Boolean(table.search || practiceFilter);

    const clearFilters = () => {
        setSearchInput('');
        handlePracticeFilterChange(null);
    };

    const handleSendPasswordReset = async (user: UserListItem) => {
        try {
            await sendPasswordReset.mutateAsync(user.email);
            showToast(`Password reset email sent to ${user.email}`, 'success');
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to send password reset email'), 'error');
        }
    };

    const drawer = useUserDrawerController({ loadedUsers: users });

    const openEditDrawer = (user: UserListItem) => drawer.openEditDrawer(user.email);
    const openDeleteConfirm = (user: UserListItem) => drawer.openDeleteConfirm(user.email);

    const refs: DataTableRefs = {
        card: tableCardRef,
        head: tableHeaderRef,
        body: tableBodyRef,
        pagination: paginationRef
    };

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <UsersTable
                users={users}
                isLoading={isLoading}
                search={table.search}
                practiceName={practiceFilter?.name ?? null}
                hasFilters={hasFilters}
                onClearFilters={clearFilters}
                onAddUser={drawer.openCreateDrawer}
                onOpenUser={openEditDrawer}
                onEditUser={openEditDrawer}
                onSendPasswordReset={handleSendPasswordReset}
                onDeleteUser={openDeleteConfirm}
                sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                mode={table.mode}
                onModeChange={table.setMode}
                pagination={{
                    page: table.page,
                    totalPages,
                    total: meta.total,
                    onPage: table.setPage
                }}
                onLoadMore={infinite.fetchNextPage}
                hasMore={infinite.hasNextPage}
                isLoadingMore={infinite.isFetchingNextPage}
                searchConfig={{
                    value: searchInput,
                    onChange: setSearchInput,
                    placeholder: 'Search users'
                }}
                toolbarSlot={
                    <PracticeFilterPill
                        selected={practiceFilter}
                        onChange={handlePracticeFilterChange}
                    />
                }
                refs={refs}
            />

            <UserDrawerHost controller={drawer} />
        </div>
    );
}
